import { ReactNode, useRef, LegacyRef, useCallback } from 'react'
import cn from 'classnames/bind'

import useTooltip from './use-tooltip'
import useCorrectPosition from './use-correct-position'

import styles from './plain-tooltip.module.scss'

const cx = cn.bind(styles)

export type PlainTooltipProps = {
  className?: string
  popupContentClassName?: string
  popupArrowClassName?: string
  text?: string | null
  content?: ReactNode | null
  children?: ReactNode | ((ref: LegacyRef<HTMLElement>) => ReactNode)
  leftEdge?: boolean
  rightEdge?: boolean
  hideWithoutOverflow?: boolean
  style?: Record<string, string>
  enableOverflow?: boolean
  disableNoWrap?: boolean
}

const PlainTooltip = ({
  className,
  popupContentClassName: pcClassName,
  popupArrowClassName: paClassName,
  children,
  text,
  content,
  leftEdge,
  rightEdge,
  hideWithoutOverflow, // скрывать tooltip, если нет переполнения (не всегда работает, лучше не использовать)
  style: st,
  enableOverflow, // не скрывать текст многоточием
  disableNoWrap,
}: PlainTooltipProps) => {
  const [{ show, style }, mouseHandlers] = useTooltip()
  const [tooltipRef, leftShift, rightShift, dropBottom, appear] = useCorrectPosition(!show)

  const elementRef = useRef<HTMLElement | null>(null)
  const overflowed = elementRef.current
    ? elementRef.current.scrollWidth > elementRef.current.clientWidth
    : false

  const popupClassName = cx(styles.tooltip, {
    dropBottom,
    appear,
  })

  const popupContentClassName = cx(styles.content, pcClassName, {
    fixed: !!text,
    leftEdge: leftEdge || leftShift,
    rightEdge: rightEdge || rightShift,
    dropBottom,
  })

  const handleElement = useCallback((element: HTMLElement | null) => {
    elementRef.current = element
  }, [])

  const arrowUpClassName = cx(styles.arrowUp, paClassName)
  const arrowClassName = cx(styles.arrow, paClassName)

  const empty = !(text || content)

  return (
    <span
      {...mouseHandlers}
      className={cx(
        { asBlock: typeof children !== 'function' && !disableNoWrap, enableOverflow },
        className,
      )}
      ref={elementRef}
      style={st}
    >
      {show && !empty && (hideWithoutOverflow ? overflowed : true) && (
        <div className={styles.container} style={style}>
          <div className={popupClassName}>
            {dropBottom && <div className={arrowUpClassName} />}

            {/* @ts-ignore */}
            <div ref={tooltipRef} className={popupContentClassName}>
              {text || content}
            </div>

            {!dropBottom && <div className={arrowClassName} />}
          </div>
        </div>
      )}
      {typeof children === 'function' ? children(handleElement) : children}
    </span>
  )
}

export default PlainTooltip
